import { styled } from "styled-components";

import { SWReactCountryIcons } from "@similarweb/icons";
import type { ColumnsType } from "@similarweb/pro-logic-components";
import { TextCell } from "@similarweb/pro-logic-components";
import { colorsPalettes } from "@similarweb/styles";
import { ChangeValue, LegendBullet, ProgressBar } from "@similarweb/ui-components";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import { changeFilter } from "app/filters/ngFilters";
import type { ICategoryComparison } from "app/pages/industry-analysis/categories-context/CategoriesContext";
import CountryService from "app/services/CountryService";

import type { CountryData, TopCountriesComparisonRow } from "./types";

const percentageFormatter = changeFilter();

const StyledLegendWrapper = styled.div`
    display: inline-block;
`;

const renderTitle = (category: ICategoryComparison) => (
    <StyledLegendWrapper>
        <LegendBullet
            labelColor={category.color}
            text={<Text fontVariant="captionTitle">{category.category.text}</Text>}
        />
    </StyledLegendWrapper>
);

const renderCell = (countryData: CountryData | null) => {
    if (!countryData) {
        return (
            <Flex alignItems="center">
                <Text
                    fontVariant="input2"
                    color={colorsPalettes.carbon[200]}
                >
                    -
                </Text>
            </Flex>
        );
    }

    const { countryCode, marketShare, change, color } = countryData;
    const countryName = CountryService.getCountryById(countryCode).text;

    return (
        <Flex alignItems="center">
            <Flex
                flex="3"
                gap="4px"
                alignItems="center"
            >
                <SWReactCountryIcons
                    countryCode={countryCode}
                    size="xs"
                />
                <Text
                    data-automation="top-countries-comparison-country-name"
                    fontVariant="input2"
                    color={colorsPalettes.carbon[400]}
                >
                    {countryName}
                </Text>
            </Flex>
            <Flex
                flex="3"
                gap="12px"
                alignItems="center"
            >
                <Flex
                    width="40px"
                    justifyContent="flex-end"
                    flexShrink={0}
                >
                    <Text
                        fontVariant="input2"
                        data-automation="top-countries-comparison-traffic-share"
                    >
                        {percentageFormatter(marketShare, 1)}
                    </Text>
                </Flex>
                <Flex flex="1">
                    <ProgressBar
                        values={[marketShare]}
                        colorSet={[color]}
                    />
                </Flex>
            </Flex>
            <Flex
                flex="2"
                justifyContent="flex-end"
                alignItems="center"
            >
                <ChangeValue
                    isDecrease={change < 0}
                    unsigned={change === 0}
                    value={percentageFormatter(change, 2)}
                />
            </Flex>
        </Flex>
    );
};

export const getColumnConfig = (categories: ICategoryComparison[]): ColumnsType<TopCountriesComparisonRow> => [
    {
        key: "playerRank",
        title: "",
        render: (_, row) => (
            <Flex justifyContent="center">
                <TextCell>{row.countryRank}</TextCell>
            </Flex>
        ),
        width: 60,
    },
    {
        key: "primaryCategory",
        title: renderTitle(categories[0]),
        render: (_, row) => renderCell(row.primaryCategory),
    },
    {
        key: "comparedCategory",
        title: renderTitle(categories[1]),
        render: (_, row) => renderCell(row.comparedCategory),
    },
];
